/**
 * Socket.io event names used throughout the application
 */
export var SocketEvent;
(function (SocketEvent) {
    // Connection events
    SocketEvent["CONNECT"] = "connect";
    SocketEvent["DISCONNECT"] = "disconnect";
    SocketEvent["ERROR"] = "error";
    // Authentication events
    SocketEvent["AUTH_REQUEST"] = "auth:request";
    SocketEvent["AUTH_SUCCESS"] = "auth:success";
    SocketEvent["AUTH_FAILURE"] = "auth:failure";
    SocketEvent["AUTH_TOKENS"] = "auth:tokens";
    // Client events
    SocketEvent["CLIENT_MESSAGE"] = "client:message";
    SocketEvent["CLIENT_UPDATE"] = "client:update";
    SocketEvent["CLIENT_STATUS"] = "client:status";
    // Admin events
    SocketEvent["ADMIN_BROADCAST"] = "admin:broadcast";
    SocketEvent["ADMIN_GET_CLIENTS"] = "admin:get-clients";
    SocketEvent["ADMIN_KICK_CLIENT"] = "admin:kick-client";
    SocketEvent["ADMIN_SEND_ALERT"] = "admin:send-alert";
    SocketEvent["ADMIN_ALERT_BROADCAST"] = "admin:alert-broadcast";
    // Admin player management events (game-scoped)
    SocketEvent["ADMIN_GET_GAME_PLAYERS"] = "admin:get-game-players";
    SocketEvent["ADMIN_GAME_PLAYERS"] = "admin:game-players";
    SocketEvent["ADMIN_TOGGLE_PLAYER"] = "admin:toggle-player";
    SocketEvent["ADMIN_PLAYER_TOGGLED"] = "admin:player-toggled";
    SocketEvent["ADMIN_BAN_PLAYER"] = "admin:ban-player";
    SocketEvent["ADMIN_PLAYER_BANNED"] = "admin:player-banned";
    SocketEvent["ADMIN_UNBAN_PLAYER"] = "admin:unban-player";
    SocketEvent["ADMIN_PLAYER_UNBANNED"] = "admin:player-unbanned";
    SocketEvent["ADMIN_GET_BANNED_PLAYERS"] = "admin:get-banned-players";
    SocketEvent["ADMIN_BANNED_PLAYERS"] = "admin:banned-players";
    // Game events
    SocketEvent["GAME_CREATE"] = "game:create";
    SocketEvent["GAME_CREATED"] = "game:created";
    SocketEvent["GAME_JOIN"] = "game:join";
    SocketEvent["GAME_JOINED"] = "game:joined";
    SocketEvent["GAME_PLAYER_JOINED"] = "game:player-joined";
    SocketEvent["GAME_PLAYER_LEFT"] = "game:player-left";
    SocketEvent["GAME_PLAYER_DISCONNECTED"] = "game:player-disconnected";
    SocketEvent["GAME_LEAVE"] = "game:leave";
    SocketEvent["GAME_LEFT"] = "game:left";
    SocketEvent["GAME_SELECT"] = "game:select";
    SocketEvent["GAME_SELECTED"] = "game:selected";
    SocketEvent["GAME_START"] = "game:start";
    SocketEvent["GAME_STARTED"] = "game:started";
    SocketEvent["GAME_END"] = "game:end";
    SocketEvent["GAME_ENDED"] = "game:ended";
    SocketEvent["GAME_LIST"] = "game:list";
    SocketEvent["GAME_DELETE"] = "game:delete";
    SocketEvent["GAME_DELETED"] = "game:deleted";
    /**
     * Fired whenever the shared Game object mutates, to each socket in the room:
     * the whole game the first time, then only what changed
     * (GameStateUpdatedMessage), with a `source` naming the trigger. Apps read it
     * through `onGameStateUpdated`; game-type-specific events stay as pure
     * notifications (no `game` field).
     */
    SocketEvent["GAME_STATE_UPDATED"] = "game:state-updated";
    /** App → Server: a patch didn't fit, send the whole game again (GameStateResyncRequest). */
    SocketEvent["GAME_STATE_RESYNC"] = "game:state-resync";
    // Round events
    SocketEvent["ROUND_CREATE"] = "round:create";
    SocketEvent["ROUND_CREATED"] = "round:created";
    SocketEvent["ROUND_LOAD"] = "round:load";
    SocketEvent["ROUND_LOADED"] = "round:loaded";
    SocketEvent["ROUND_UNLOAD"] = "round:unload";
    SocketEvent["ROUND_UNLOADED"] = "round:unloaded";
    SocketEvent["ROUND_START"] = "round:start";
    SocketEvent["ROUND_STARTED"] = "round:started";
    SocketEvent["ROUND_END"] = "round:end";
    SocketEvent["ROUND_ENDED"] = "round:ended";
    SocketEvent["ROUND_CANCEL"] = "round:cancel";
    SocketEvent["ROUND_CANCELLED"] = "round:cancelled";
    SocketEvent["ROUND_DISABLE"] = "round:disable";
    SocketEvent["ROUND_DISABLED"] = "round:disabled";
    SocketEvent["ROUND_FORCE_RESULTS"] = "round:force-results";
    SocketEvent["ROUND_FORCE_RESULTS_UPDATED"] = "round:force-results-updated";
    SocketEvent["ROUND_EDIT"] = "round:edit";
    SocketEvent["ROUND_EDITED"] = "round:edited";
    SocketEvent["ROUND_DELETE"] = "round:delete";
    SocketEvent["ROUND_DELETED"] = "round:deleted";
    SocketEvent["ROUND_UPDATE_PLAYER"] = "round:update-player";
    SocketEvent["ROUND_PLAYER_UPDATED"] = "round:player-updated";
    SocketEvent["ROUND_HIDING_COMPLETE"] = "round:hiding-complete";
    SocketEvent["ROUND_TIME_UP"] = "round:time-up";
    // Combat log events
    SocketEvent["COMBAT_LOG_EVENT"] = "combat:log-event";
    SocketEvent["COMBAT_LOG_STATUS"] = "combat:log-status";
    // Player update events
    SocketEvent["PLAYER_CHARACTER_NAME"] = "player:character-name";
    SocketEvent["PLAYER_CHARACTER_NAME_UPDATED"] = "player:character-name-updated";
    SocketEvent["PLAYER_AVATAR_SELECT"] = "player:avatar-select";
    // Tagging events (Hide and Seek)
    SocketEvent["PLAYER_TAGGED"] = "player:tagged";
    SocketEvent["PLAYER_TAGGED_BROADCAST"] = "player:tagged-broadcast";
    // FP Race events
    SocketEvent["FPR_RACE_BEGUN"] = "fpr:race-begun";
    SocketEvent["FPR_PLAYER_DEATH"] = "fpr:player-death";
    SocketEvent["FPR_FLASHPOINT_ENTERED"] = "fpr:flashpoint-entered";
    SocketEvent["FPR_WRONG_DIFFICULTY_BROADCAST"] = "fpr:wrong-difficulty-broadcast";
    SocketEvent["FPR_TEAM_ENTRIES_WAITING"] = "fpr:team-entries-waiting";
    SocketEvent["FPR_TEAM_ENTRIES_WAITING_BROADCAST"] = "fpr:team-entries-waiting-broadcast";
    SocketEvent["FPR_BOSS_KILLED"] = "fpr:boss-killed";
    SocketEvent["FPR_BOSS_KILLED_BROADCAST"] = "fpr:boss-killed-broadcast";
    SocketEvent["FPR_TEAM_STARTED"] = "fpr:team-started";
    SocketEvent["FPR_TEAM_STARTED_BROADCAST"] = "fpr:team-started-broadcast";
    SocketEvent["FPR_TEAM_START_TIME_ADJUSTED"] = "fpr:team-start-time-adjusted";
    SocketEvent["FPR_TEAM_START_TIME_ADJUSTED_BROADCAST"] = "fpr:team-start-time-adjusted-broadcast";
    SocketEvent["FPR_AUTHORITATIVE_PLAYER_CHANGED_BROADCAST"] = "fpr:authoritative-player-changed-broadcast";
    SocketEvent["FPR_TEAM_FINISHED"] = "fpr:team-finished";
    SocketEvent["FPR_TEAM_FINISHED_BROADCAST"] = "fpr:team-finished-broadcast";
    SocketEvent["FPR_TEAM_DNF"] = "fpr:team-dnf";
    SocketEvent["FPR_TEAM_DNF_BROADCAST"] = "fpr:team-dnf-broadcast";
    SocketEvent["FPR_PROGRESS_UPDATE"] = "fpr:progress-update";
    SocketEvent["FPR_ALL_TEAMS_FINISHED"] = "fpr:all-teams-finished";
    SocketEvent["FPR_SCORING_FAILED"] = "fpr:scoring-failed";
    SocketEvent["FPR_ADMIN_SET_TEAM_STATUS"] = "fpr:admin-set-team-status";
    SocketEvent["FPR_ADMIN_RETRY_SCORING"] = "fpr:admin:retry-scoring";
    SocketEvent["FPR_ADMIN_FORCE_COMPLETE_ROUND"] = "fpr:admin:force-complete-round";
    SocketEvent["FPR_ADMIN_GET_RESULTS"] = "fpr:admin:get-results";
    SocketEvent["FPR_ADMIN_RESULTS"] = "fpr:admin:results";
    SocketEvent["FPR_REQUEST_FLASHPOINT_CATALOGUE"] = "fpr:request-flashpoint-catalogue";
    // FP Race Composition Rules events
    SocketEvent["FPR_PLAYER_DISCIPLINE_REPORTED"] = "fpr:player-discipline-reported";
    SocketEvent["FPR_TEAM_COMPOSITION_VIOLATION"] = "fpr:team-composition-violation";
    SocketEvent["FPR_TEAM_COMPOSITION_CLEARED"] = "fpr:team-composition-cleared";
    // FP Race Team Management events
    SocketEvent["FPR_CREATE_TEAM"] = "fpr:create-team";
    SocketEvent["FPR_TEAM_CREATED"] = "fpr:team-created";
    SocketEvent["FPR_UPDATE_TEAM"] = "fpr:update-team";
    SocketEvent["FPR_TEAM_UPDATED"] = "fpr:team-updated";
    SocketEvent["FPR_DELETE_TEAM"] = "fpr:delete-team";
    SocketEvent["FPR_TEAM_DELETED"] = "fpr:team-deleted";
    SocketEvent["FPR_JOIN_TEAM"] = "fpr:join-team";
    SocketEvent["FPR_PLAYER_JOINED_TEAM"] = "fpr:player-joined-team";
    SocketEvent["FPR_LEAVE_TEAM"] = "fpr:leave-team";
    SocketEvent["FPR_PLAYER_LEFT_TEAM"] = "fpr:player-left-team";
    SocketEvent["FPR_LOCK_TEAMS"] = "fpr:lock-teams";
    SocketEvent["FPR_TEAMS_LOCKED"] = "fpr:teams-locked";
    SocketEvent["FPR_UNLOCK_TEAMS"] = "fpr:unlock-teams";
    SocketEvent["FPR_TEAMS_UNLOCKED"] = "fpr:teams-unlocked";
    // Trailblazers (TRL) gameplay events
    SocketEvent["TRL_DIG_ATTEMPT"] = "trl:dig-attempt";
    SocketEvent["TRL_TREASURE_FOUND"] = "trl:treasure-found";
    SocketEvent["TRL_DIG_CLOSE"] = "trl:dig-close";
    SocketEvent["TRL_DIG_MISS"] = "trl:dig-miss";
    SocketEvent["TRL_DIG_IGNORED"] = "trl:dig-ignored";
    SocketEvent["TRL_TREASURE_FOUND_BROADCAST"] = "trl:treasure-found-broadcast";
    // Trailblazers (TRL) team management events
    SocketEvent["TRL_CREATE_TEAM"] = "trl:create-team";
    SocketEvent["TRL_TEAM_CREATED"] = "trl:team-created";
    SocketEvent["TRL_UPDATE_TEAM"] = "trl:update-team";
    SocketEvent["TRL_TEAM_UPDATED"] = "trl:team-updated";
    SocketEvent["TRL_DELETE_TEAM"] = "trl:delete-team";
    SocketEvent["TRL_TEAM_DELETED"] = "trl:team-deleted";
    SocketEvent["TRL_ASSIGN_PLAYER"] = "trl:assign-player";
    SocketEvent["TRL_REMOVE_PLAYER"] = "trl:remove-player";
    SocketEvent["TRL_SET_TEAM_CAPTAIN"] = "trl:set-team-captain";
    SocketEvent["TRL_UPDATE_TEAM_NAME_CONFIG"] = "trl:update-team-name-config";
    SocketEvent["TRL_TEAM_NAME_CONFIG_UPDATED"] = "trl:team-name-config-updated";
    SocketEvent["TRL_RESYNC_DISCORD"] = "trl:resync-discord";
    SocketEvent["TRL_DISCORD_RESYNCED"] = "trl:discord-resynced";
    // Trailblazers (TRL) stalemate-clue events
    SocketEvent["TRL_UPDATE_STALEMATE_CONFIG"] = "trl:update-stalemate-config";
    SocketEvent["TRL_STALEMATE_CONFIG_UPDATED"] = "trl:stalemate-config-updated";
    SocketEvent["TRL_ARM_SCANNER"] = "trl:arm-scanner";
    SocketEvent["TRL_SCANNER_ARMED"] = "trl:scanner-armed";
    SocketEvent["TRL_STALEMATE_CLUE_REVEALED"] = "trl:stalemate-clue-revealed";
    SocketEvent["TRL_DIG_DISTANCE"] = "trl:dig-distance";
    // Trailblazers (TRL) radar-range events
    SocketEvent["TRL_UPDATE_RADAR_CONFIG"] = "trl:update-radar-config";
    SocketEvent["TRL_RADAR_CONFIG_UPDATED"] = "trl:radar-config-updated";
    // Trailblazers (TRL) triangulation events
    SocketEvent["TRL_UPDATE_TRIANGULATION_CONFIG"] = "trl:update-triangulation-config";
    SocketEvent["TRL_TRIANGULATION_CONFIG_UPDATED"] = "trl:triangulation-config-updated";
    SocketEvent["TRL_SCAN_STARTED"] = "trl:scan-started";
    SocketEvent["TRL_SCAN_CANCELLED"] = "trl:scan-cancelled";
    SocketEvent["TRL_TRIANGULATION_STATE"] = "trl:triangulation-state";
    SocketEvent["TRL_SCAN_COMPLETED"] = "trl:scan-completed";
    // Trailblazers (TRL) disruption events
    SocketEvent["TRL_UPDATE_DISRUPTION_CONFIG"] = "trl:update-disruption-config";
    SocketEvent["TRL_DISRUPTION_CONFIG_UPDATED"] = "trl:disruption-config-updated";
    SocketEvent["TRL_SPARKLE_HIT"] = "trl:sparkle-hit";
    SocketEvent["TRL_DISRUPTION_STATE"] = "trl:disruption-state";
    SocketEvent["TRL_DISRUPTION_REJECTED"] = "trl:disruption-rejected";
    SocketEvent["TRL_DIG_BLOCKED"] = "trl:dig-blocked";
    SocketEvent["TRL_ADMIN_CLEAR_DISRUPTION"] = "trl:admin-clear-disruption";
    // Trailblazers (TRL) team scanner-activity events
    SocketEvent["TRL_TEAM_ACTIVITY"] = "trl:team-activity";
    SocketEvent["TRL_REQUEST_TEAM_ACTIVITY"] = "trl:request-team-activity";
    // Hide and Seek location plan
    SocketEvent["HNS_UPDATE_LOCATION_PLAN"] = "hns:update-location-plan";
    SocketEvent["HNS_UPDATE_ROUND_DEFAULTS"] = "hns:update-round-defaults";
    // Hide and Seek results
    SocketEvent["HNS_ADMIN_GET_RESULTS"] = "hns:admin:get-results";
    SocketEvent["HNS_ADMIN_RESULTS"] = "hns:admin:results";
    // Hide and Seek Teams (HNST). Requests are answered on their ack (HnstActionResponse).
    SocketEvent["HNST_SET_CAPTAIN"] = "hnst:set-captain";
    SocketEvent["HNST_ASSIGN_PLAYER"] = "hnst:assign-player";
    SocketEvent["HNST_CONFIGURE_DRAFT"] = "hnst:configure-draft";
    SocketEvent["HNST_START_DRAFT"] = "hnst:start-draft";
    SocketEvent["HNST_DRAFT_PICK"] = "hnst:draft-pick";
    SocketEvent["HNST_CANCEL_DRAFT"] = "hnst:cancel-draft";
    SocketEvent["HNST_CREATE_COIN_TOSS"] = "hnst:create-coin-toss";
    SocketEvent["HNST_UPDATE_COIN_TOSS"] = "hnst:update-coin-toss";
    SocketEvent["HNST_DELETE_COIN_TOSS"] = "hnst:delete-coin-toss";
    SocketEvent["HNST_FLIP_COIN"] = "hnst:flip-coin";
    SocketEvent["HNST_COIN_CHOICE"] = "hnst:coin-choice";
    SocketEvent["HNST_CONFIGURE_COUNTDOWNS"] = "hnst:configure-countdowns";
    SocketEvent["HNST_STAGE_PREVIEW"] = "hnst:stage-preview";
    SocketEvent["HNST_ADMIN_GET_RESULTS"] = "hnst:admin:get-results";
    SocketEvent["HNST_ADMIN_RESULTS"] = "hnst:admin:results";
    // Game results, every game type (admin → server, answered by ack; by game id, so archived games work)
    SocketEvent["RESULTS_ADMIN_GET_STATUS"] = "results:admin:get-status";
    SocketEvent["RESULTS_ADMIN_PUBLISH"] = "results:admin:publish";
    SocketEvent["RESULTS_ADMIN_UNPUBLISH"] = "results:admin:unpublish";
    SocketEvent["RESULTS_ADMIN_REBUILD"] = "results:admin:rebuild";
    SocketEvent["RESULTS_ADMIN_SET_AUTO_PUBLISH"] = "results:admin:set-auto-publish";
    SocketEvent["RESULTS_ADMIN_LIST_DISCORD_TARGETS"] = "results:admin:list-discord-targets";
    SocketEvent["RESULTS_ADMIN_POST_DISCORD"] = "results:admin:post-discord";
    // Game rules page, every game type (admin → server, answered by ack; by game id). /game posts it to Discord.
    SocketEvent["RULES_ADMIN_GET_STATUS"] = "rules:admin:get-status";
    SocketEvent["RULES_ADMIN_PUBLISH"] = "rules:admin:publish";
    SocketEvent["RULES_ADMIN_UNPUBLISH"] = "rules:admin:unpublish";
    SocketEvent["RULES_ADMIN_UPDATE_INFO"] = "rules:admin:update-info";
    // Asset package events (every game type). See types/assetPackage.ts.
    SocketEvent["ASSETS_REQUEST_PACKAGE"] = "assets:request-package";
    SocketEvent["ASSETS_PACKAGE_CHANGED"] = "assets:package-changed";
    SocketEvent["ASSETS_PACKAGE_STATUS"] = "assets:package-status";
    SocketEvent["ASSETS_STATUS"] = "assets:status";
    SocketEvent["ASSETS_REQUEST_STATUS"] = "assets:request-status";
    // Player-location events (every game type). Locations never ride the game
    // snapshot: each viewer is sent only what its game type's policy allows
    // (see api-server socket/playerLocations.ts).
    SocketEvent["PLAYER_REPORT_LOCATION"] = "player:report-location";
    SocketEvent["PLAYER_LOCATIONS"] = "player:locations";
    SocketEvent["PLAYER_REQUEST_LOCATIONS"] = "player:request-locations";
    /** @deprecated Sent by clients older than 0.39; handled as PLAYER_REPORT_LOCATION. Remove once MIN_CLIENT_VERSION >= 0.39. */
    SocketEvent["TRL_REPORT_LOCATION"] = "trl:report-location";
    // Trailblazers (TRL) sabotage events (pre-game planet sabotage; distinct from disruption)
    SocketEvent["TRL_UPDATE_SABOTAGE_CONFIG"] = "trl:update-sabotage-config";
    SocketEvent["TRL_SABOTAGE_CONFIG_UPDATED"] = "trl:sabotage-config-updated";
    SocketEvent["TRL_PLACE_SABOTAGE"] = "trl:place-sabotage";
    SocketEvent["TRL_REMOVE_SABOTAGE"] = "trl:remove-sabotage";
    SocketEvent["TRL_DIG_DECOY"] = "trl:dig-decoy";
    SocketEvent["TRL_SABOTAGE_TRIGGERED"] = "trl:sabotage-triggered";
    // Trailblazers (TRL) treasure-reward events. Distinct from TRL_TREASURE_FOUND
    // above, which is about capturing a node rather than winning a reward item.
    SocketEvent["TRL_UPDATE_TREASURE_CONFIG"] = "trl:update-treasure-config";
    SocketEvent["TRL_TREASURE_CONFIG_UPDATED"] = "trl:treasure-config-updated";
    SocketEvent["TRL_TREASURE_AWARDED"] = "trl:treasure-awarded";
    /** @deprecated Clients from 0.39 get these icons in the room asset package. Remove once MIN_CLIENT_VERSION >= 0.39. */
    SocketEvent["TRL_REQUEST_TREASURE_ICONS"] = "trl:request-treasure-icons";
    // Trailblazers (TRL) galaxy-map design events
    SocketEvent["TRL_SAVE_MAP"] = "trl:save-map";
    SocketEvent["TRL_LOAD_MAP"] = "trl:load-map";
    SocketEvent["TRL_MAP_UPDATED"] = "trl:map-updated";
    // Trailblazers (TRL) start-point selection events
    SocketEvent["TRL_START_POINTS_REVEALED"] = "trl:start-points-revealed";
    SocketEvent["TRL_SELECT_START_NODE"] = "trl:select-start-node";
    SocketEvent["TRL_START_NODE_SELECTED"] = "trl:start-node-selected";
    SocketEvent["TRL_LOCK_START_NODE"] = "trl:lock-start-node";
    SocketEvent["TRL_START_NODE_LOCKED"] = "trl:start-node-locked";
    // Trailblazers (TRL) progression events
    SocketEvent["TRL_NODES_REVEALED"] = "trl:nodes-revealed";
    SocketEvent["TRL_NODE_CLEARED"] = "trl:node-cleared";
    SocketEvent["TRL_SELECT_NEXT_NODE"] = "trl:select-next-node";
    SocketEvent["TRL_CHECKPOINT_REACHED"] = "trl:checkpoint-reached";
    SocketEvent["TRL_CHECKPOINT_RELEASED"] = "trl:checkpoint-released";
    SocketEvent["TRL_RELEASE_CHECKPOINT"] = "trl:release-checkpoint";
    SocketEvent["TRL_TEAM_FINISHED"] = "trl:team-finished";
    // Trailblazers (TRL) admin live-game overrides. Escape hatches for a stalled
    // game (a bug, a disconnection, a team that simply cannot find the image) —
    // every one is gated by resolveTrlAdminGame (owning admin only).
    SocketEvent["TRL_ADMIN_FORCE_CLEAR_NODE"] = "trl:admin-force-clear-node";
    SocketEvent["TRL_ADMIN_SET_CURRENT_NODE"] = "trl:admin-set-current-node";
    SocketEvent["TRL_ADMIN_MARK_TEAM_FINISHED"] = "trl:admin-mark-team-finished";
    SocketEvent["TRL_ADMIN_REVEAL_CLUE"] = "trl:admin-reveal-clue";
    SocketEvent["TRL_ADMIN_RESEND_SNAPSHOT"] = "trl:admin-resend-snapshot";
    // Trailblazers admin statistics snapshot (admin-web Results tab).
    SocketEvent["TRL_ADMIN_GET_RESULTS"] = "trl:admin:get-results";
    SocketEvent["TRL_ADMIN_RESULTS"] = "trl:admin:results";
    // Disqualification events (Cheat Detection)
    SocketEvent["PLAYER_DISQUALIFIED"] = "player:disqualified";
    SocketEvent["PLAYER_DISQUALIFIED_BROADCAST"] = "player:disqualified-broadcast";
    /** Server → room: a Hide and Seek Teams hider was eliminated by too many deaths (PlayerEliminatedBroadcast). */
    SocketEvent["PLAYER_ELIMINATED_BROADCAST"] = "player:eliminated-broadcast";
    SocketEvent["ADMIN_DISQUALIFY_PLAYER"] = "admin:disqualify-player";
    SocketEvent["ADMIN_REVOKE_DISQUALIFICATION"] = "admin:revoke-disqualification";
    SocketEvent["PLAYER_DISQUALIFICATION_REVOKED"] = "player:disqualification-revoked";
    // Max Camera Distance (Hide and Seek rounds that limit it)
    /** Electron → server: the player's SWTOR Max Camera Distance (PlayerCameraDistanceReport). */
    SocketEvent["PLAYER_CAMERA_DISTANCE"] = "player:camera-distance";
    /** Admin → server: switch a player's camera check off or on for a round (AdminSetCameraCheckRequest). */
    SocketEvent["ADMIN_SET_CAMERA_CHECK"] = "admin:set-camera-check";
    // Debug events
    SocketEvent["TOGGLE_CLIENT_DEBUG"] = "debug:toggle-client";
    SocketEvent["CLIENT_DEBUG_TOGGLED"] = "debug:client-toggled";
    // Marco Polo events
    SocketEvent["MARCO_POLO_ACTIVATE"] = "marco-polo:activate";
    SocketEvent["MARCO_POLO_ACTIVATED"] = "marco-polo:activated";
    SocketEvent["MARCO_POLO_CANCEL"] = "marco-polo:cancel";
    SocketEvent["MARCO_POLO_CANCELLED"] = "marco-polo:cancelled";
    SocketEvent["MARCO_POLO_RESPONSE"] = "marco-polo:response";
    SocketEvent["MARCO_POLO_RESPONSE_BROADCAST"] = "marco-polo:response-broadcast";
    SocketEvent["MARCO_POLO_UPDATE"] = "marco-polo:update";
    // Round Events (HnS Ruleset Change + Marco Polo timeline)
    SocketEvent["ROUND_EVENT_SCHEDULE"] = "round-event:schedule";
    SocketEvent["ROUND_EVENT_UPDATE"] = "round-event:update";
    SocketEvent["ROUND_EVENT_DELETE"] = "round-event:delete";
    SocketEvent["ROUND_EVENT_APPLY_NOW"] = "round-event:apply-now";
    SocketEvent["ROUND_EVENT_LIST"] = "round-event:list";
    SocketEvent["ROUND_EVENT_SCHEDULED"] = "round-event:scheduled";
    SocketEvent["ROUND_EVENT_UPDATED"] = "round-event:updated";
    SocketEvent["ROUND_EVENT_DELETED"] = "round-event:deleted";
    SocketEvent["ROUND_EVENT_WARNING"] = "round-event:warning";
    SocketEvent["ROUND_EVENT_FIRED"] = "round-event:fired";
    // Krall's Accord (HnS automatic location tracking) events
    SocketEvent["KRALLS_ACCORD_STATUS_UPDATE"] = "kralls-accord:status-update";
    SocketEvent["KRALLS_ACCORD_STATUS_BROADCAST"] = "kralls-accord:status-broadcast";
    SocketEvent["KRALLS_ACCORD_PATH_UPLOAD"] = "kralls-accord:path-upload";
    /** A round's Krall's Accord paths, once it has ended (HnsRoundPathsRequest, answered by ack). */
    SocketEvent["HNS_ROUND_PATHS_GET"] = "hns:round-paths-get";
    /** Server → room: a round's Krall's Accord paths changed (HnsRoundPathsUpdated). */
    SocketEvent["HNS_ROUND_PATHS_UPDATED"] = "hns:round-paths-updated";
    // Discord events (admin)
    SocketEvent["DISCORD_GET_AUTH_URL"] = "discord:get-auth-url";
    SocketEvent["DISCORD_AUTH_URL"] = "discord:auth-url";
    SocketEvent["DISCORD_LINK_COMPLETE"] = "discord:link-complete";
    SocketEvent["DISCORD_LINK_RESULT"] = "discord:link-result";
    SocketEvent["DISCORD_UNLINK"] = "discord:unlink";
    SocketEvent["DISCORD_UNLINK_RESULT"] = "discord:unlink-result";
    SocketEvent["DISCORD_GET_GUILDS"] = "discord:get-guilds";
    SocketEvent["DISCORD_GUILDS_LIST"] = "discord:guilds-list";
    SocketEvent["DISCORD_SELECT_GUILD"] = "discord:select-guild";
    SocketEvent["DISCORD_GUILD_SELECTED"] = "discord:guild-selected";
    SocketEvent["DISCORD_GET_CHANNELS"] = "discord:get-channels";
    SocketEvent["DISCORD_CHANNELS_LIST"] = "discord:channels-list";
    SocketEvent["DISCORD_GET_ROLES"] = "discord:get-roles";
    SocketEvent["DISCORD_ROLES_LIST"] = "discord:roles-list";
    SocketEvent["DISCORD_UPDATE_GAME_SETTINGS"] = "discord:update-game-settings";
    SocketEvent["DISCORD_SETTINGS_UPDATED"] = "discord:settings-updated";
    SocketEvent["DISCORD_GET_BOT_INVITE"] = "discord:get-bot-invite";
    SocketEvent["DISCORD_BOT_INVITE"] = "discord:bot-invite";
    SocketEvent["DISCORD_GET_STATUS"] = "discord:get-status";
    SocketEvent["DISCORD_STATUS"] = "discord:status";
    SocketEvent["DISCORD_SEND_TEST_ALERT"] = "discord:send-test-alert";
    SocketEvent["DISCORD_TEST_ALERT_RESULT"] = "discord:test-alert-result";
    // Discord events (client/player)
    SocketEvent["DISCORD_PLAYER_LINK"] = "discord:player-link";
    SocketEvent["DISCORD_PLAYER_LINKED"] = "discord:player-linked";
    // Discord events (admin manual linking)
    SocketEvent["DISCORD_ADMIN_LINK_PLAYER"] = "discord:admin-link-player";
    SocketEvent["DISCORD_GET_MEMBERS"] = "discord:get-members";
    SocketEvent["DISCORD_MEMBERS_LIST"] = "discord:members-list";
    // Visual Effects events (server -> client)
    SocketEvent["EFFECT_TRIGGER"] = "effect:trigger";
    SocketEvent["EFFECT_CLEAR"] = "effect:clear";
    // Effect Settings events (admin)
    SocketEvent["ADMIN_UPDATE_EFFECT_SETTINGS"] = "admin:update-effect-settings";
    SocketEvent["EFFECT_SETTINGS_UPDATED"] = "effect:settings-updated";
    SocketEvent["ADMIN_PREVIEW_EFFECT"] = "admin:preview-effect";
    SocketEvent["ADMIN_PREVIEW_CINEMATIC"] = "admin:preview-cinematic";
    // Game status transitions (admin)
    SocketEvent["ADMIN_UPDATE_GAME_STATUS"] = "admin:update-game-status";
    SocketEvent["GAME_STATUS_UPDATED"] = "game:status-updated";
    // Admin-tunable round_ending status duration
    SocketEvent["ADMIN_UPDATE_ROUND_ENDING_DURATION"] = "admin:update-round-ending-duration";
    SocketEvent["ROUND_ENDING_DURATION_UPDATED"] = "game:round-ending-duration-updated";
    // Registration list / read events
    SocketEvent["REGISTRATIONS_LIST"] = "registrations:list";
    SocketEvent["REGISTRATIONS_DATA"] = "registrations:data";
    SocketEvent["REGISTRATION_ADDED"] = "registrations:added";
    SocketEvent["REGISTRATION_REMOVED"] = "registrations:removed";
    /** Admin adds / removes a sign-up by hand (ack: RegistrationChangeAck) */
    SocketEvent["REGISTRATION_ADD"] = "registrations:add";
    SocketEvent["REGISTRATION_REMOVE"] = "registrations:remove";
    // Registration export / import (admin)
    SocketEvent["EXPORT_REGISTRATIONS_REQUEST"] = "registrations:export-request";
    SocketEvent["EXPORT_REGISTRATIONS_DATA"] = "registrations:export-data";
    SocketEvent["IMPORT_REGISTRATIONS"] = "registrations:import";
    SocketEvent["REGISTRATIONS_IMPORTED"] = "registrations:imported";
    // FPR player registration management (admin manual entry)
    SocketEvent["FPR_SET_PLAYER_REGISTRATION"] = "fpr:set-player-registration";
    SocketEvent["FPR_PLAYER_REGISTRATION_SET"] = "fpr:player-registration-set";
    SocketEvent["FPR_CLAIM_DISCORD_REGISTRATION"] = "fpr:claim-discord-registration";
    // FPR leader selection phase
    SocketEvent["FPR_GET_LEADER_SUGGESTIONS"] = "fpr:get-leader-suggestions";
    SocketEvent["FPR_LEADER_SUGGESTIONS"] = "fpr:leader-suggestions";
    // FPR Pool Creation events
    SocketEvent["FPR_COMPUTE_POOLS"] = "fpr:compute-pools";
    SocketEvent["FPR_POOLS_COMPUTED"] = "fpr:pools-computed";
    SocketEvent["FPR_CONFIRM_POOLS"] = "fpr:confirm-pools";
    SocketEvent["FPR_POOLS_CONFIRMED"] = "fpr:pools-confirmed";
    SocketEvent["FPR_UPDATE_POOL_ASSIGNMENT"] = "fpr:update-pool-assignment";
    SocketEvent["FPR_POOL_ASSIGNMENT_UPDATED"] = "fpr:pool-assignment-updated";
    // FPR Draft system events
    SocketEvent["FPR_CONFIGURE_DRAFT"] = "fpr:configure-draft";
    SocketEvent["FPR_DRAFT_CONFIGURED"] = "fpr:draft-configured";
    SocketEvent["FPR_START_RANDOM_DRAFT"] = "fpr:start-random-draft";
    SocketEvent["FPR_RANDOM_DRAFT_RESULT"] = "fpr:random-draft-result";
    SocketEvent["FPR_START_SNAKE_DRAFT"] = "fpr:start-snake-draft";
    SocketEvent["FPR_SNAKE_DRAFT_STARTED"] = "fpr:snake-draft-started";
    SocketEvent["FPR_SNAKE_DRAFT_PICK"] = "fpr:snake-draft-pick";
    SocketEvent["FPR_SNAKE_DRAFT_STATE"] = "fpr:snake-draft-state";
    SocketEvent["FPR_SKIP_SNAKE_PICK"] = "fpr:skip-snake-pick";
    SocketEvent["FPR_CANCEL_SNAKE_DRAFT"] = "fpr:cancel-snake-draft";
    SocketEvent["FPR_COMPUTE_TL_ROLES"] = "fpr:compute-tl-roles";
    SocketEvent["FPR_TL_ROLES_COMPUTED"] = "fpr:tl-roles-computed";
    // Dev / testing events (only active in debug mode)
    SocketEvent["DEV_SEED_DUMMY_PLAYERS"] = "dev:seed-dummy-players";
    SocketEvent["DEV_DUMMY_PLAYERS_SEEDED"] = "dev:dummy-players-seeded";
    // Version events (server → client)
    SocketEvent["VERSION_INFO"] = "version:info";
    // The server's clock (client → server, answered by ack: TimeSyncResponse)
    SocketEvent["TIME_SYNC"] = "time:sync";
})(SocketEvent || (SocketEvent = {}));
